import React, { useState, useCallback } from 'react';
import Calibracao from './components/Calibracao';
import Medicao from './components/Medicao';
import FormularioPonto from './components/FormularioPonto';
import Resultados from './components/Resultados';
import AdSlot from './components/AdSlot';
import './styles/App.css';

/**
 * Sonômetro Digital - Aplicativo React
 * Medição de ruído ambiental conforme NBR 10151
 */
function App() {
  // Estado da aba ativa
  const [activeTab, setActiveTab] = useState('medicao');
  
  // Offset de calibração
  const [calibrationOffset, setCalibrationOffset] = useState(() => {
    const saved = localStorage.getItem('sonometro_calibration');
    return saved ? parseFloat(saved) : 0;
  });
  
  // Dados da medição atual
  const [currentMedicao, setCurrentMedicao] = useState(null);
  
  // Pontos de medição
  const [pontos, setPontos] = useState(() => {
    const saved = localStorage.getItem('sonometro_pontos');
    return saved ? JSON.parse(saved) : [];
  });

  /**
   * Callback quando a calibração é completada
   */
  const handleCalibrationComplete = useCallback((offset) => {
    setCalibrationOffset(offset);
    localStorage.setItem('sonometro_calibration', offset.toString());
  }, []);

  /**
   * Callback quando uma medição é completada
   */
  const handleMeasurementComplete = useCallback((data) => {
    setCurrentMedicao(data);
    // Muda para aba de dados se não estiver nela
    if (activeTab !== 'dados') {
      setActiveTab('dados');
    }
  }, [activeTab]);

  /**
   * Limpa dados da medição atual
   */
  const clearCurrentMedicao = useCallback(() => {
    setCurrentMedicao(null);
  }, []);

  /**
   * Adiciona novo ponto
   */
  const handleAddPonto = useCallback((ponto) => {
    setPontos(prev => {
      const updated = [...prev, ponto];
      localStorage.setItem('sonometro_pontos', JSON.stringify(updated));
      return updated;
    });
    setCurrentMedicao(null);
  }, []);

  /**
   * Remove ponto pelo índice
   */
  const handleRemovePonto = useCallback((index) => {
    setPontos(prev => {
      const updated = prev.filter((_, i) => i !== index);
      localStorage.setItem('sonometro_pontos', JSON.stringify(updated));
      return updated;
    });
  }, []);

  /**
   * Limpa todos os pontos
   */
  const handleClearAll = useCallback(() => {
    if (window.confirm('Deseja realmente limpar todos os pontos de medição?')) {
      setPontos([]);
      localStorage.removeItem('sonometro_pontos');
    }
  }, []);

  return (
    <div className="app">
      {/* Header */}
      <header className="app-header">
        <div className="header-content">
          <h1>📊 Sonômetro Digital</h1>
          <p>Medição de Ruído Ambiental - NBR 10151</p>
        </div>
        <div className="header-actions">
          {calibrationOffset !== 0 && (
            <div className="calibration-indicator">
              Calibrado: {calibrationOffset > 0 ? '+' : ''}{calibrationOffset} dB
            </div>
          )}
          <a href="https://andersonstaub.com/apps" className="more-apps-link" target="_blank" rel="noopener noreferrer">
            Mais Apps →
          </a>
        </div>
      </header>

      {/* Ad slot - Header */}
      <AdSlot position="header-inner" />

      {/* Navegação por abas */}
      <nav className="tabs">
        <button 
          className={`tab ${activeTab === 'medicao' ? 'active' : ''}`}
          onClick={() => setActiveTab('medicao')}
        >
          🎤 Medição
        </button>
        <button 
          className={`tab ${activeTab === 'dados' ? 'active' : ''}`}
          onClick={() => setActiveTab('dados')}
        >
          📝 Dados
          {currentMedicao && <span className="badge">!</span>}
        </button>
        <button 
          className={`tab ${activeTab === 'resultados' ? 'active' : ''}`}
          onClick={() => setActiveTab('resultados')}
        >
          📊 Resultados
          {pontos.length > 0 && <span className="badge">{pontos.length}</span>}
        </button>
        <button 
          className={`tab ${activeTab === 'calibracao' ? 'active' : ''}`}
          onClick={() => setActiveTab('calibracao')}
        >
          ⚙️ Calibração
        </button>
      </nav>

      {/* Conteúdo das abas */}
      <main className="main-content">
        {activeTab === 'medicao' && (
          <Medicao 
            calibrationOffset={calibrationOffset}
            onMeasurementComplete={handleMeasurementComplete}
          />
        )}

        {activeTab === 'dados' && (
          <FormularioPonto 
            onAddPonto={handleAddPonto}
            medicaoData={currentMedicao}
            onClearMedicao={clearCurrentMedicao}
          />
        )}

        {activeTab === 'resultados' && (
          <Resultados 
            pontos={pontos}
            onRemovePonto={handleRemovePonto}
            onClearAll={handleClearAll}
          />
        )}

        {activeTab === 'calibracao' && (
          <Calibracao 
            onCalibrationComplete={handleCalibrationComplete}
            currentOffset={calibrationOffset}
          />
        )}

        {/* Ad slot - Content */}
        <AdSlot position="content-inner" />
      </main>

      {/* Footer */}
      <footer className="app-footer">
        {/* Ad slot - Footer */}
        <AdSlot position="footer-inner" />
        
        <p>Sonômetro Digital v1.0 | Conforme NBR 10151:2019</p>
        <p className="disclaimer">
          ⚠️ As medições realizadas com dispositivos móveis são indicativas. 
          Para laudos técnicos, utilize equipamentos homologados e calibrados.
        </p>
        <p className="copyright">
          &copy; {new Date().getFullYear()} Anderson R. Staub. Todos os direitos reservados.
        </p>
      </footer>
    </div>
  );
}

export default App;
